import { useQueryClient } from "@tanstack/react-query";
import type { SearchReturnRequestsResponseDto } from "../generated/schema";
import { getPublicAuthHeader } from "./return-widget-client";
import { usePublicApiMutation } from "./use-public-api-mutation";

type Params = {
	returnRequestId: string;
};

const SEARCH_QUERY_KEY = ["get", "/public/return-request/search"] as const;

/**
 * Cancels a return request from the public portal.
 *
 * The hook deliberately does NOT touch the `/public/return-request/search`
 * cache on success: the search endpoint excludes cancelled returns, so any
 * cache update (or refetch) removes the return request that the details page
 * is still displaying, and that page would throw. Callers must leave the
 * details route first, then call `purgeCancelledReturnRequest`.
 */
export function usePublicCancelReturnRequest() {
	const queryClient = useQueryClient();

	const mutation = usePublicApiMutation(
		"delete",
		"/public/return-request/{returnRequestId}",
		{
			mapVariables: ({ returnRequestId }: Params) => ({
				params: {
					path: { returnRequestId },
					header: getPublicAuthHeader(),
				},
			}),
		},
	);

	const purgeCancelledReturnRequest = (returnRequestId: string) => {
		queryClient.setQueriesData<SearchReturnRequestsResponseDto>(
			{ queryKey: SEARCH_QUERY_KEY },
			(old) => {
				if (!old) return old;
				return {
					...old,
					returnRequests: old.returnRequests.filter((rr) => rr.id !== returnRequestId),
				};
			},
		);
		return queryClient.invalidateQueries({ queryKey: SEARCH_QUERY_KEY });
	};

	return { ...mutation, purgeCancelledReturnRequest };
}
